import { supabase } from "@/integrations/supabase/client";
import {
  getEntitlementOverrideUserId,
  resetEntitlementOverride,
  setEntitlementOverride,
} from "@/lib/entitlementOverrideStore";
import { isProSetupDone, openProSetupFlow } from "./proSetupFlow";
import { isEntitlementActive } from "./entitlementActive";
import { clearPersistedLimitFlag } from "@/hooks/useMessageLimit";

// v1.3 spec §A.2-A.4: app-shell entitlement flip detection.
//
// useAuth.tsx is a locked file for this feature, so the "current tier" it
// holds in React state can't be written to directly. Instead this module
// keeps its own lightweight override store: once a free->pro flip is
// detected, `entitlementOverride` flips to true and every subscriber
// (useIsPro, useFeature) re-renders as Pro without useAuth.tsx changing.
//
// User-scoping (July 11 2026 fix): the override is a bare module singleton,
// so without this it would survive a sign-out inside the same tab and leak
// onto whichever account signs in next — a free account landing in the same
// tab right after a Pro account would silently inherit Pro everywhere
// (Family Profile edit, Kin's Initiative slider, voice). Every flip is now
// tagged with the userId that earned it, and this module listens to
// supabase.auth.onAuthStateChange directly (not via useAuth.tsx, same
// out-of-scope constraint as the rest of this file) to null the override
// the moment the signed-in user stops matching that id — on sign-out, and
// on sign-in as anyone else.

export const CHECKOUT_STARTED_KEY = "kin_checkout_started";
export const CHECKOUT_MAX_AGE_MS = 30 * 60 * 1000; // 30 minutes
export const POLL_INTERVAL_MS = 5000; // 5 seconds
export const POLL_MAX_MS = 60000; // 60 seconds

// #632 — the store itself now lives in entitlementOverrideStore.ts so that
// useAuth.tsx can read it without importing this file (which would close a
// cycle back through useMessageLimit). This module keeps the supabase/auth
// wiring and the flip policy; it owns WHEN the override is set, not where it
// is kept.

// Belt-and-suspenders: SIGNED_OUT always resets (covers session becoming
// null even if something upstream ever changes the null-session event
// shape), and any sign-in as a userId other than the one that set the
// override resets it too. A same-user TOKEN_REFRESHED/USER_UPDATED event
// leaves it alone.
supabase.auth.onAuthStateChange((event, session) => {
  const currentUserId = session?.user?.id ?? null;
  const mismatchedUser =
    getEntitlementOverrideUserId() !== null && currentUserId !== getEntitlementOverrideUserId();
  if (event === "SIGNED_OUT" || mismatchedUser) {
    resetEntitlementOverride();
  }
});

// Re-exported so every existing call site keeps importing from here. The
// store module is an implementation detail of WHERE the value lives; this is
// still the front door for reading it.
export {
  subscribeEntitlementOverride,
  getEntitlementOverrideSnapshot,
  useEntitlementOverride,
} from "@/lib/entitlementOverrideStore";

/**
 * Same query shape as the session-start entitlements fetch in useAuth.tsx
 * (select tier/expires_at/admin_preview, filter by user_id, maybeSingle,
 * same activePro expression). Duplicated rather than imported because
 * useAuth.tsx is out of scope for this change.
 */
export async function fetchActiveProTier(userId: string): Promise<boolean> {
  try {
    const { data } = await supabase
      .from("entitlements")
      .select("tier, expires_at, admin_preview")
      .eq("user_id", userId)
      .maybeSingle();
    return isEntitlementActive(data);
  } catch {
    return false;
  }
}

/** spec §A.3 step 3: opens the Pro Setup Flow celebration modal, unless she's
 * already been through it (full skip or completed — see spec §B.1). */
export function openProWelcome(): void {
  if (isProSetupDone()) return;
  openProSetupFlow();
}

/** Call once a free->pro flip is confirmed for `userId`. Refreshes the
 * entitlements override (so useFeature/useIsPro gates re-render live),
 * clears the in-flight checkout flag, and hands off to the (stubbed)
 * welcome flow. The override is tagged to `userId` — see the
 * onAuthStateChange listener above, which nulls it out again the moment
 * the signed-in user stops being this same id.
 *
 * `openWelcome` (default true, so every existing caller is unchanged) exists
 * only because EntitlementFlipListener now also runs this check on a cold
 * start, where "confirmed Pro" no longer implies "just bought" — a mom who
 * has been Pro for weeks confirms Pro on every single launch, and she must
 * not be handed the setup wizard each time. THE UNLOCK IS NEVER CONDITIONAL:
 * the override, the cap flag and the checkout flag are cleared either way.
 * This flag gates the modal and nothing else. */
export function fireEntitlementFlipped(
  userId: string,
  { openWelcome = true }: { openWelcome?: boolean } = {},
): void {
  setEntitlementOverride(userId);
  // #379: "Unlimited messages" is Pro's headline benefit, and hitting the free
  // 15/day cap IS the upgrade trigger — so the cap flag must not outlive the
  // upgrade. Index.tsx exempts Pro from the client block on this same render;
  // this drops the persisted kin_limit_reached record so she also stays
  // unblocked across a force-quit instead of waiting for local midnight. The
  // server never blocked her (messageCapLogic checks isPro) — the stale flag is
  // purely client-side.
  clearPersistedLimitFlag();
  try {
    localStorage.removeItem(CHECKOUT_STARTED_KEY);
  } catch {
    /* ignore */
  }
  if (openWelcome) openProWelcome();
}
